import React from 'react';
import type { Step } from 'react-joyride';

function TourBody({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rc-tour__body">
      <h3 className="rc-tour__title">{title}</h3>
      <p className="rc-tour__text">{children}</p>
    </div>
  );
}

const base: Partial<Step> = {
  placement: 'right',
};

/**
 * Job seeker tour — also used when a working professional switches to
 * "Want a Referral" mode (nav slugs are identical in both variants).
 */
export const JOBSEEKER_TOUR_STEPS: Step[] = [
  {
    ...base,
    target: 'body',
    placement: 'center',
    content: (
      <TourBody title="Welcome to ReffConnect 👋">
        Your referral-powered job search starts here. Let us show you around — it takes about
        30 seconds.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-jobseeker-dashboard"]',
    content: (
      <TourBody title="Your dashboard">
        A live snapshot of your job search — profile strength, recent activity, and quick
        actions to get you moving.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-resume-grader"]',
    content: (
      <TourBody title="AI Resume Grader">
        Get instant AI feedback on your resume and close the gaps before you apply anywhere.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-group-services"]',
    content: (
      <TourBody title="Expert services">
        Book resume reviews, mock interviews, and referral sessions with professionals working
        at your target companies.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-jobopenings"]',
    content: (
      <TourBody title="Job openings">
        Browse roles where insiders are ready to refer you — and apply with a real advantage.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-my-sessions"]',
    content: (
      <TourBody title="My sessions">
        Every booking — upcoming and completed — tracked in one place.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="topbar-search"]',
    placement: 'bottom',
    content: (
      <TourBody title="Quick search">
        Find services, referrers, and openings instantly from anywhere on your dashboard.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="notifications"]',
    placement: 'bottom',
    content: (
      <TourBody title="Notifications">
        Booking confirmations and referrer replies land here — keep an eye on the dot.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="hired-button"]',
    placement: 'bottom',
    content: (
      <TourBody title="Got hired? 🎉">
        Tell us when you land the job — you&apos;ll unlock the Working Professional dashboard
        and can start referring (and earning) yourself.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="tour-replay"]',
    placement: 'bottom',
    content: (
      <TourBody title="That's the tour!">
        Replay this guide anytime from this compass. Good luck out there — your next role is
        one referral away.
      </TourBody>
    ),
  },
];

/** Working professional (referrer) tour. */
export const EMPLOYEE_TOUR_STEPS: Step[] = [
  {
    ...base,
    target: 'body',
    placement: 'center',
    content: (
      <TourBody title="Welcome to your professional workspace 👋">
        Refer great candidates, offer expert services, and earn — here&apos;s a quick tour of
        how it all works.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-referral-dashboard"]',
    content: (
      <TourBody title="Your dashboard">
        Earnings, upcoming sessions, and referral activity — your whole practice at a glance.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-services"]',
    content: (
      <TourBody title="My services">
        Configure what you offer — resume reviews, mock interviews, referral sessions — and set
        your price and availability.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-post-job"]',
    content: (
      <TourBody title="Post a job">
        Share openings from your company and attract candidates you can refer.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-payouts"]',
    content: (
      <TourBody title="Payouts">
        Track what you&apos;ve earned and withdraw whenever you like.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-my-sessions"]',
    content: (
      <TourBody title="My sessions">
        Your booked sessions with candidates — upcoming and past — all in one place.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="mode-switcher"]',
    content: (
      <TourBody title="Wearing two hats?">
        Job-hunting yourself? Switch to &quot;Want a Referral&quot; anytime — you get the full
        job seeker toolkit too.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="notifications"]',
    placement: 'bottom',
    content: (
      <TourBody title="Notifications">
        New booking requests and candidate messages land here.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="tour-replay"]',
    placement: 'bottom',
    content: (
      <TourBody title="That's the tour!">
        Replay this guide anytime from this compass. Happy referring!
      </TourBody>
    ),
  },
];
